"use client";
import React, { useEffect, useCallback, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import ReactPlayer from "react-player";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Portal from "src/app/components/Portal";

const isYouTube = (url: string): boolean => /youtube\.com|youtu\.be/.test(url);
const isBitChute = (url: string): boolean => /bitchute\.com/.test(url);
// const placeholderBlur = "/default-placeholder.png";

type MediaItem =
  | { type: "image"; src: string }
  | { type: "video"; src: string }
  | { type: "placeholder"; src: string };

interface FullscreenCarouselProps {
  items: MediaItem[];
  initialIndex?: number;
  onClose: () => void;
  showNavButtons?: boolean;
}

const FullscreenCarousel: React.FC<FullscreenCarouselProps> = ({
  items,
  initialIndex = 0,
  onClose,
  showNavButtons = true,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    startIndex: initialIndex,
    containScroll: "keepSnaps",
    loop: true,
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  return (
    <Portal>
      <div className="fixed inset-0 z-[5000] bg-black flex items-center justify-center">
        <div className="relative w-full h-full max-w-[90%]">
          <div className="embla h-full overflow-hidden" ref={emblaRef}>
            <div className="embla__container flex h-full">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="embla__slide flex-shrink-0 w-full relative h-full"
                  style={{ minWidth: "100%" }}
                >
                  {item.type === "image" && (
                    <Image
                      src={item.src}
                      alt=""
                      fill
                      className="object-contain"
                    />
                  )}

                  {item.type === "video" && isYouTube(item.src) && (
                    <ReactPlayer
                      url={item.src}
                      controls
                      width="100%"
                      height="100%"
                    />
                  )}

                  {item.type === "video" && isBitChute(item.src) && (
                    <iframe
                      src={item.src.replace("/video/", "/embed/")}
                      title="BitChute Video"
                      width="100%"
                      height="100%"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {showNavButtons && (
            <>
              <button
                onClick={() => emblaApi?.scrollPrev()}
                className="absolute top-1/2 left-4 transform -translate-y-1/2 text-white bg-black/40 rounded-full p-2"
              >
                <ChevronLeft />
              </button>
              <button
                onClick={() => emblaApi?.scrollNext()}
                className="absolute top-1/2 right-4 transform -translate-y-1/2 text-white bg-black/40 rounded-full p-2"
              >
                <ChevronRight />
              </button>
            </>
          )}

          <button
            onClick={onClose}
            className="absolute z-[100000] top-4 right-1 text-white text-3xl"
          >
            ×
          </button>
        </div>
      </div>
    </Portal>
  );
};

export default FullscreenCarousel;
